import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getCancelledInvoicesDisclaimer } from 'lib/api/factoring';
import SectionCard from 'lib/common/GeneralComponents/TenderModal/Components/SectionCard';
import AlertBanner from 'lib/designSystem/AlertBanner';
import PageHeader from 'lib/designSystem/PageHeader';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { DOCUMENT_SUB_TYPE_MAP } from 'pages/Finance/Components/Funds/FactoringFundReSell/components/NewMultiResell/components/BatchImporter';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

type CancelledInvoicesDetailsProps = {
  businessIdentifier: string;
};

function CancelledInvoicesDetails({
  businessIdentifier,
}: CancelledInvoicesDetailsProps) {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);

  const { data } = getCancelledInvoicesDisclaimer(dispatch, businessIdentifier);

  if (!data || data.cancelledInvoices.length === 0) return null;

  const totalAmountCancelled = data.cancelledInvoices.reduce((acc, i) => {
    return acc + i.amount;
  }, 0);

  const cancelledInvoicesByRequestId = data.cancelledInvoices.reduce(
    (acc: Record<number, typeof data.cancelledInvoices>, i) => {
      if (!acc[i.factoringRequestId]) acc[i.factoringRequestId] = [];
      acc[i.factoringRequestId].push(i);
      return acc;
    },
    {},
  );

  return (
    <>
      <AlertBanner
        severity="error"
        title="Cliente con facturas rechazadas en operación"
        message={`Se encontraron ${data.cancelledInvoices.length} documentos rechazados en
        operaciones.`}
        action={
          <LightTooltip
            title={<Typography padding={1}>{'Más información'}</Typography>}
          >
            <IconButton
              size="small"
              onClick={() => setOpenDialog(true)}
              color="error"
              aria-label="Remove business"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </LightTooltip>
        }
      />
      <Dialog
        maxWidth="md"
        fullWidth
        scroll="paper"
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>
          <PageHeader.TitleOnly
            iconType="info"
            // iconVariant="warning"
            title="Facturas rechazadas en operación"
            variant="dialogHeader"
          />
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {Object.entries(cancelledInvoicesByRequestId).map(
              ([requestId, invoices]) => (
                <SectionCard
                  key={requestId}
                  title={`Solicitud #${requestId}`}
                  chipLabel={invoices[0].status}
                >
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Folio</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Fecha Emisión</TableCell>
                          <TableCell>Fecha Rechazo</TableCell>
                          <TableCell>Monto</TableCell>
                          <TableCell>Obs. Cobranza</TableCell>
                        </TableRow>
                      </TableHead>

                      {invoices.map((invoice, invoiceId) => (
                        <TableBody key={invoiceId}>
                          <TableRow>
                            <TableCell>{invoice.folio}</TableCell>
                            <TableCell>
                              {DOCUMENT_SUB_TYPE_MAP[invoice.subType ?? ''] ??
                                invoice.subType}
                            </TableCell>
                            <TableCell>
                              {formatDate(
                                new Date(invoice.issuedDate),
                                'dd-mm-yyyy',
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(
                                new Date(invoice.cancelDate),
                                'dd-mm-yyyy',
                              )}
                            </TableCell>
                            <TableCell>{formatMoney(invoice.amount)}</TableCell>
                            <TableCell>
                              {invoice.lastCollectionManagementDescription ??
                                '—'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ))}
                      <TableBody>
                        <TableRow sx={{ fontWeight: 'bold' }}>
                          <TableCell colSpan={5}>
                            <strong>Total</strong>
                          </TableCell>
                          <TableCell>
                            <strong>
                              {formatMoney(
                                invoices.reduce(
                                  (sum, inv) => sum + inv.amount,
                                  0,
                                ),
                              )}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionCard>
              ),
            )}
          </Box>
          {Object.keys(cancelledInvoicesByRequestId).length > 1 && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1">
                <strong>Total: </strong>
                <strong>{formatMoney(totalAmountCancelled)}</strong>
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CancelledInvoicesDetails;
